/**
 * Home screen — warm, personal welcome with medication overview.
 * Compact, visual design that minimizes scrolling.
 * Uses localStorage-backed patient data from db.ts (not SQL).
 */

import { createVoiceButton } from '../components/voice-button';
import type { PatientMedication, DoseLog } from '../../types/index';
import {
  getPatientMedications,
  getTodayDoseLogs,
  getPatientName,
  logDose,
  undoLastDose,
  type PatientMedRecord,
  type DoseLogRecord,
} from '../../core/db';
import { t, getLang, setLang, LANGUAGES, LANG_ORDER, type LangCode } from '../../i18n';
import { stop as stopSpeech, speak } from '../../voice/tts';

// -----------------------------------------------------------------------
// Data helpers
// -----------------------------------------------------------------------

interface QuickStatus {
  totalMeds: number;
  takenToday: number;
  remaining: number;
  totalDosesToday: number;
}

interface NextDose {
  drugName: string;
  strength: string;
  time: string;
  instructions: string;
  minutesUntil: number;
  medId: string;
}

function toPatientMed(rec: PatientMedRecord): PatientMedication {
  return {
    id: rec.id,
    drug_rxcui: rec.drug_rxcui,
    drug_name: rec.drug_name,
    strength: rec.strength,
    frequency: rec.frequency as PatientMedication['frequency'],
    times: rec.times,
    instructions: rec.instructions,
    active: rec.active === 1,
  };
}

function toDoseLog(rec: DoseLogRecord): DoseLog {
  return {
    id: rec.id,
    med_id: rec.med_id,
    action: rec.action as DoseLog['action'],
    scheduled_time: rec.scheduled_time,
    actual_time: rec.actual_time,
  };
}

function computeNextDose(meds: PatientMedication[]): NextDose | null {
  const now = new Date();
  const nowMinutes = now.getHours() * 60 + now.getMinutes();
  let best: NextDose | null = null;
  let bestMinutes = Infinity;

  for (const med of meds) {
    for (const timeStr of med.times) {
      const [h, m] = timeStr.split(':').map(Number);
      const medMinutes = h * 60 + m;
      let diff = medMinutes - nowMinutes;
      if (diff < -30) diff += 1440;
      if (diff >= 0 && diff < bestMinutes) {
        bestMinutes = diff;
        best = {
          drugName: med.drug_name,
          strength: med.strength,
          time: timeStr,
          instructions: med.instructions,
          minutesUntil: diff,
          medId: med.id,
        };
      }
    }
  }

  return best;
}

function computeStatus(meds: PatientMedication[], logs: DoseLog[]): QuickStatus {
  const totalMeds = meds.length;
  const takenToday = logs.filter((l) => l.action === 'taken').length;
  let totalDosesToday = 0;
  for (const med of meds) {
    totalDosesToday += med.times.length;
  }
  return {
    totalMeds,
    takenToday,
    remaining: Math.max(0, totalDosesToday - takenToday),
    totalDosesToday,
  };
}

function formatTimeUntil(minutes: number): string {
  if (minutes <= 0) return t('home.now');
  if (minutes < 60) return t('home.minutes', { n: minutes });
  const hrs = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (mins === 0) return t('home.hours', { n: hrs });
  return t('home.hoursMinutes', { h: hrs, m: mins });
}

function formatTime12h(time24: string): string {
  const [h, m] = time24.split(':').map(Number);
  const period = h >= 12 ? 'PM' : 'AM';
  const hour12 = h % 12 || 12;
  return `${hour12}:${String(m).padStart(2, '0')} ${period}`;
}

function getGreeting(): string {
  const hour = new Date().getHours();
  if (hour < 5) return t('home.greeting.night');
  if (hour < 12) return t('home.greeting.morning');
  if (hour < 17) return t('home.greeting.afternoon');
  if (hour < 21) return t('home.greeting.evening');
  return t('home.greeting.night');
}

// -----------------------------------------------------------------------
// Pill image + purpose maps (shared data)
// -----------------------------------------------------------------------

const PILL_IMAGE_MAP: Record<string, string> = {
  'metformin': '/pill-images/metformin-500.svg',
  'lisinopril': '/pill-images/lisinopril-10.svg',
  'atorvastatin': '/pill-images/atorvastatin-20.svg',
  'omeprazole': '/pill-images/omeprazole-20.svg',
};

function getPillImage(drugName: string): string {
  const lower = drugName.toLowerCase();
  for (const [key, path] of Object.entries(PILL_IMAGE_MAP)) {
    if (lower.includes(key)) return path;
  }
  return '/pill-images/default-pill.svg';
}

const DRUG_PURPOSE_KEY_MAP: Record<string, string> = {
  'metformin': 'purpose.bloodSugar',
  'lisinopril': 'purpose.bloodPressure',
  'atorvastatin': 'purpose.cholesterol',
  'omeprazole': 'purpose.stomachAcid',
  'amlodipine': 'purpose.bloodPressure',
  'losartan': 'purpose.bloodPressure',
  'hydrochlorothiazide': 'purpose.bloodPressure',
  'gabapentin': 'purpose.nervePain',
  'sertraline': 'purpose.moodAnxiety',
  'levothyroxine': 'purpose.thyroid',
  'acetaminophen': 'purpose.painRelief',
  'ibuprofen': 'purpose.painSwelling',
  'aspirin': 'purpose.heartProtection',
  'warfarin': 'purpose.bloodClots',
  'furosemide': 'purpose.fluidRemoval',
  'prednisone': 'purpose.inflammation',
  'albuterol': 'purpose.breathing',
  'insulin': 'purpose.bloodSugar',
  'clopidogrel': 'purpose.bloodClots',
  'pantoprazole': 'purpose.stomachAcid',
};

function getDrugPurpose(drugName: string): string {
  const lower = drugName.toLowerCase();
  for (const [key, purposeKey] of Object.entries(DRUG_PURPOSE_KEY_MAP)) {
    if (lower.includes(key)) return t(purposeKey);
  }
  return '';
}

// Drug color coding for visual identification
const DRUG_COLOR_MAP: Record<string, string> = {
  'metformin': '#3d9d8f',
  'lisinopril': '#5b8bd6',
  'atorvastatin': '#9b7dc4',
  'omeprazole': '#d4885c',
};

function getDrugColor(drugName: string): string {
  const lower = drugName.toLowerCase();
  for (const [key, color] of Object.entries(DRUG_COLOR_MAP)) {
    if (lower.includes(key)) return color;
  }
  return '#8b95a5';
}

// -----------------------------------------------------------------------
// Compact progress ring
// -----------------------------------------------------------------------

function createProgressRing(taken: number, total: number): HTMLElement {
  const wrapper = document.createElement('div');
  wrapper.style.cssText = 'display: grid; place-items: center;';

  const ringContainer = document.createElement('div');
  ringContainer.style.cssText = 'grid-area: 1/1; display: grid; place-items: center;';

  const size = 72;
  const strokeWidth = 6;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const pct = total > 0 ? taken / total : 0;
  const offset = circumference * (1 - pct);

  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('width', String(size));
  svg.setAttribute('height', String(size));
  svg.setAttribute('viewBox', `0 0 ${size} ${size}`);
  svg.setAttribute('role', 'img');
  svg.setAttribute('aria-label', `${taken} of ${total} doses taken today`);
  svg.style.cssText = 'transform: rotate(-90deg); grid-area: 1/1;';

  const bgCircle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
  bgCircle.setAttribute('cx', String(size / 2));
  bgCircle.setAttribute('cy', String(size / 2));
  bgCircle.setAttribute('r', String(radius));
  bgCircle.setAttribute('fill', 'none');
  bgCircle.setAttribute('stroke', 'rgba(61,157,143,0.12)');
  bgCircle.setAttribute('stroke-width', String(strokeWidth));
  svg.appendChild(bgCircle);

  const progressCircle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
  progressCircle.setAttribute('cx', String(size / 2));
  progressCircle.setAttribute('cy', String(size / 2));
  progressCircle.setAttribute('r', String(radius));
  progressCircle.setAttribute('fill', 'none');
  progressCircle.setAttribute('stroke', pct >= 1 ? '#4aad4e' : '#3d9d8f');
  progressCircle.setAttribute('stroke-width', String(strokeWidth));
  progressCircle.setAttribute('stroke-linecap', 'round');
  progressCircle.setAttribute('stroke-dasharray', String(circumference));
  progressCircle.setAttribute('stroke-dashoffset', String(offset));
  progressCircle.style.cssText = 'transition: stroke-dashoffset 0.8s ease;';
  svg.appendChild(progressCircle);

  ringContainer.appendChild(svg);

  const counter = document.createElement('div');
  counter.style.cssText = 'grid-area: 1/1; text-align: center;';

  const countNum = document.createElement('div');
  countNum.style.cssText = 'font-size: 20px; font-weight: 700; color: var(--color-text); line-height: 1;';
  countNum.textContent = `${taken}/${total}`;
  counter.appendChild(countNum);

  const countLabel = document.createElement('div');
  countLabel.style.cssText = 'font-size: 10px; color: var(--color-text-muted); margin-top: 1px;';
  countLabel.textContent = t('home.doses');
  counter.appendChild(countLabel);

  ringContainer.appendChild(counter);
  wrapper.appendChild(ringContainer);
  return wrapper;
}

// -----------------------------------------------------------------------
// Compact pill tile (for 2x2 grid)
// -----------------------------------------------------------------------

function createPillTile(med: PatientMedication, logs: DoseLog[], container: HTMLElement): HTMLElement {
  const takenToday = logs.some(
    (l) => l.action === 'taken' && l.med_id === med.id,
  );

  const tile = document.createElement('div');
  tile.style.cssText = `
    background: var(--color-bg-card);
    border-radius: 16px;
    padding: 14px;
    display: flex; flex-direction: column; align-items: center;
    gap: 6px;
    border: 1.5px solid ${takenToday ? 'var(--color-success)' : getDrugColor(med.drug_name) + '30'};
    position: relative;
    cursor: pointer;
    transition: transform 0.15s ease;
    box-shadow: 0 1px 4px rgba(0,0,0,0.05);
    ${takenToday ? 'opacity: 0.7;' : ''}
  `;

  // Taken checkmark overlay
  if (takenToday) {
    const check = document.createElement('div');
    check.style.cssText = 'position: absolute; top: 6px; right: 6px; font-size: 16px;';
    check.textContent = '\u2705';
    tile.appendChild(check);
  }

  // Pill image
  const img = document.createElement('img');
  img.src = getPillImage(med.drug_name);
  img.alt = med.drug_name + ' pill';
  img.style.cssText = 'width: 48px; height: 48px; object-fit: contain;';
  img.onerror = () => { img.onerror = null; img.src = '/pill-images/default-pill.svg'; };
  tile.appendChild(img);

  // Drug name (short)
  const shortName = med.drug_name.split(' ')[0]; // "Metformin" from "Metformin 500mg"
  const nameEl = document.createElement('div');
  nameEl.style.cssText = 'font-size: 16px; font-weight: 700; color: var(--color-text); text-align: center; line-height: 1.2;';
  nameEl.textContent = shortName;
  tile.appendChild(nameEl);

  // Purpose tag
  const purpose = getDrugPurpose(med.drug_name);
  if (purpose) {
    const tag = document.createElement('div');
    tag.style.cssText = `font-size: 13px; color: ${getDrugColor(med.drug_name)}; font-weight: 600; text-align: center;`;
    tag.textContent = purpose;
    tile.appendChild(tag);
  }

  // Next time
  const timeEl = document.createElement('div');
  timeEl.style.cssText = 'font-size: 13px; color: var(--color-text-muted); text-align: center;';
  timeEl.textContent = med.times.map(formatTime12h).join(' & ');
  tile.appendChild(timeEl);

  // Action button
  if (!takenToday) {
    const btn = document.createElement('button');
    btn.style.cssText = `
      margin-top: 6px; padding: 10px 0; width: 100%;
      border-radius: 8px; border: none;
      background: ${getDrugColor(med.drug_name)};
      color: #fff; font-size: 15px; font-weight: 700;
      cursor: pointer; font-family: var(--font-family);
      touch-action: manipulation; min-height: 44px;
    `;
    btn.textContent = t('home.tookIt');
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      logDose(med.id, 'taken');
      btn.textContent = '\u2705';
      setTimeout(() => renderHomeScreen(container), 400);
    });
    tile.appendChild(btn);
  } else {
    const undoBtn = document.createElement('button');
    undoBtn.style.cssText = `
      margin-top: 6px; padding: 10px 0; width: 100%;
      border-radius: 8px; border: 1px solid var(--color-border);
      background: transparent; color: var(--color-text-muted);
      font-size: 13px; cursor: pointer; font-family: var(--font-family);
      min-height: 44px;
    `;
    undoBtn.textContent = t('home.undo');
    undoBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      undoLastDose(med.id);
      renderHomeScreen(container);
    });
    tile.appendChild(undoBtn);
  }

  // Navigate to detail on tile tap
  tile.setAttribute('role', 'button');
  tile.setAttribute('tabindex', '0');
  tile.setAttribute('aria-label', `${med.drug_name} — ${getDrugPurpose(med.drug_name) || ''}`);
  tile.addEventListener('click', () => {
    window.location.hash = '#drug/' + encodeURIComponent(med.drug_rxcui);
  });
  tile.addEventListener('keydown', (e: KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      window.location.hash = '#drug/' + encodeURIComponent(med.drug_rxcui);
    }
  });

  return tile;
}

// -----------------------------------------------------------------------
// Render — compact, visual home screen
// -----------------------------------------------------------------------

// Track cleanup functions to prevent event listener leaks
let homeScreenCleanup: (() => void)[] = [];

export async function renderHomeScreen(container: HTMLElement): Promise<void> {
  // Clean up previous listeners
  for (const cleanup of homeScreenCleanup) cleanup();
  homeScreenCleanup = [];

  container.textContent = '';
  container.className = 'screen';

  const wrapper = document.createElement('div');
  wrapper.className = 'flex-col';
  wrapper.style.cssText = 'max-width: 600px; margin: 0 auto; gap: 12px;';

  // Load data
  const medRecords = getPatientMedications();
  const logRecords = getTodayDoseLogs();
  const meds = medRecords.map(toPatientMed);
  const logs = logRecords.map(toDoseLog);
  const status = computeStatus(meds, logs);
  const patientName = getPatientName();

  // ---- Row 1: Greeting + Progress ring (side by side) ----
  const topRow = document.createElement('div');
  topRow.style.cssText = 'display: flex; align-items: center; justify-content: space-between; padding: var(--space-sm) var(--space-xs) 0;';

  const greetingBlock = document.createElement('div');
  greetingBlock.style.cssText = 'flex: 1;';

  const greeting = document.createElement('h1');
  greeting.style.cssText = 'margin: 0; font-size: 22px; color: var(--color-text); line-height: 1.2;';
  greeting.textContent = patientName
    ? `${getGreeting()}, ${patientName}`
    : getGreeting();
  greetingBlock.appendChild(greeting);

  const tagline = document.createElement('p');
  tagline.style.cssText = 'margin: 4px 0 0; font-size: 13px; color: var(--color-primary); font-weight: 500;';
  tagline.textContent = t('home.tagline');
  greetingBlock.appendChild(tagline);

  topRow.appendChild(greetingBlock);

  if (meds.length > 0) {
    topRow.appendChild(createProgressRing(status.takenToday, status.totalDosesToday));
  }

  wrapper.appendChild(topRow);

  // ---- Language selector (flag bar) ----
  const langSection = document.createElement('div');
  langSection.style.cssText = 'display: flex; flex-direction: column; gap: 2px; padding: 4px var(--space-xs);';

  const langLabel = document.createElement('span');
  langLabel.style.cssText = 'font-size: 13px; color: var(--color-text-dim); text-transform: uppercase; letter-spacing: 0.5px;';
  langLabel.textContent = t('home.selectLanguage');
  langSection.appendChild(langLabel);

  const langBar = document.createElement('div');
  langBar.style.cssText = 'display: flex; align-items: center; gap: 4px; justify-content: space-evenly;';

  for (const code of LANG_ORDER) {
    const meta = LANGUAGES[code];
    const isActive = code === getLang();

    const flagWrapper = document.createElement('button');
    flagWrapper.style.cssText = `
      display: flex; align-items: center; justify-content: center;
      padding: 8px 10px; border-radius: 12px;
      border: 2px solid ${isActive ? 'var(--color-primary)' : 'transparent'};
      background: ${isActive ? 'rgba(61,157,143,0.12)' : 'transparent'};
      cursor: pointer; transition: all 0.2s ease; touch-action: manipulation;
      min-width: 44px; min-height: 44px;
    `;
    flagWrapper.title = meta.nativeName;
    flagWrapper.setAttribute('aria-label', `${meta.nativeName} (${meta.name})`);

    const flagEmoji = document.createElement('span');
    flagEmoji.style.cssText = 'font-size: 24px; line-height: 1;';
    flagEmoji.textContent = meta.flag;
    flagWrapper.appendChild(flagEmoji);

    flagWrapper.addEventListener('click', () => {
      // Stop any currently playing audio/speech immediately
      stopSpeech();
      setLang(code as LangCode);
      // Re-render the entire home screen to reflect new language
      renderHomeScreen(container);
      // Speak a greeting in the new language via TTS
      const name = getPatientName();
      const greetingText = name
        ? `${getGreeting()}, ${name}`
        : getGreeting();
      speak(greetingText).catch(() => {});
    });
    langBar.appendChild(flagWrapper);
  }
  langSection.appendChild(langBar);
  wrapper.appendChild(langSection);

  // ---- Next dose banner (compact) ----
  const nextDose = computeNextDose(meds);
  if (nextDose) {
    const nextBanner = document.createElement('div');
    nextBanner.style.cssText = `
      display: flex; align-items: center; gap: 12px;
      padding: 12px 16px; border-radius: 14px;
      background: linear-gradient(135deg, rgba(61,157,143,0.08), rgba(61,157,143,0.03));
      border: 1px solid rgba(61,157,143,0.2);
    `;

    const nextPill = document.createElement('img');
    nextPill.src = getPillImage(nextDose.drugName);
    nextPill.alt = nextDose.drugName;
    nextPill.style.cssText = 'width: 40px; height: 40px; object-fit: contain; flex-shrink: 0;';
    nextPill.onerror = () => { nextPill.src = '/pill-images/default-pill.svg'; };
    nextBanner.appendChild(nextPill);

    const nextText = document.createElement('div');
    nextText.style.cssText = 'flex: 1; min-width: 0;';

    const nextLabel = document.createElement('div');
    nextLabel.style.cssText = 'font-size: 11px; color: var(--color-primary); font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px;';
    nextLabel.textContent = t('home.upNext');
    nextText.appendChild(nextLabel);

    const nextName = document.createElement('div');
    nextName.style.cssText = 'font-size: 16px; font-weight: 700; color: var(--color-text);';
    nextName.textContent = nextDose.drugName;
    nextText.appendChild(nextName);

    nextBanner.appendChild(nextText);

    const timeBadge = document.createElement('div');
    timeBadge.style.cssText = 'flex-shrink: 0; padding: 6px 12px; border-radius: 20px; background: var(--color-primary); color: #fff; font-size: 13px; font-weight: 700;';
    timeBadge.textContent = formatTimeUntil(nextDose.minutesUntil);
    nextBanner.appendChild(timeBadge);

    wrapper.appendChild(nextBanner);
  }

  // ---- EMR Card (prominent position — right after next dose) ----
  const emrCard = document.createElement('div');
  emrCard.style.cssText = `
    display: flex; align-items: center; gap: 14px;
    padding: 16px 18px; border-radius: 16px;
    background: linear-gradient(135deg, rgba(61,157,143,0.12), rgba(91,139,214,0.08));
    border: 2px solid rgba(61,157,143,0.35);
    cursor: pointer; transition: transform 0.15s ease;
    box-shadow: 0 2px 8px rgba(61,157,143,0.1);
  `;
  emrCard.setAttribute('role', 'button');
  emrCard.setAttribute('tabindex', '0');
  emrCard.setAttribute('aria-label', t('home.healthRecord'));
  emrCard.addEventListener('click', () => {
    window.location.hash = '#emr';
  });
  emrCard.addEventListener('keydown', (e: KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      window.location.hash = '#emr';
    }
  });

  const emrIcon = document.createElement('span');
  emrIcon.style.cssText = 'font-size: 32px; flex-shrink: 0;';
  emrIcon.textContent = '\uD83D\uDCCB';
  emrCard.appendChild(emrIcon);

  const emrText = document.createElement('div');
  emrText.style.cssText = 'flex: 1; min-width: 0;';

  const emrTitle = document.createElement('div');
  emrTitle.style.cssText = 'font-size: 16px; font-weight: 700; color: var(--color-text); line-height: 1.3;';
  emrTitle.textContent = t('home.healthRecord');
  emrText.appendChild(emrTitle);

  const emrDesc = document.createElement('div');
  emrDesc.style.cssText = 'font-size: 13px; color: var(--color-text-muted); margin-top: 3px;';
  emrDesc.textContent = t('home.healthRecordDesc');
  emrText.appendChild(emrDesc);

  emrCard.appendChild(emrText);

  const emrArrow = document.createElement('span');
  emrArrow.style.cssText = 'font-size: 20px; color: var(--color-primary); flex-shrink: 0; font-weight: 700;';
  emrArrow.textContent = '\u203A';
  emrCard.appendChild(emrArrow);

  wrapper.appendChild(emrCard);

  // ---- Medication grid (2x2 compact tiles) ----
  if (meds.length > 0) {
    const gridLabel = document.createElement('div');
    gridLabel.style.cssText = 'font-size: 13px; font-weight: 600; color: var(--color-text-muted); padding: 0 var(--space-xs); text-transform: uppercase; letter-spacing: 0.5px;';
    gridLabel.textContent = t('home.yourMedications', { n: meds.length });
    wrapper.appendChild(gridLabel);

    const grid = document.createElement('div');
    grid.style.cssText = 'display: grid; grid-template-columns: 1fr 1fr; gap: 10px;';

    for (const med of meds) {
      grid.appendChild(createPillTile(med, logs, container));
    }
    wrapper.appendChild(grid);
  }

  // ---- Voice section (compact) ----
  const voiceRow = document.createElement('div');
  voiceRow.style.cssText = `
    display: flex; align-items: center; gap: 14px;
    padding: 14px 18px; border-radius: 16px;
    background: linear-gradient(135deg, rgba(245,240,234,0.9), rgba(61,157,143,0.08));
    border: 1px solid rgba(61,157,143,0.15);
  `;

  const voiceButton = createVoiceButton(
    () => {
      voiceButton.setState('listening');
      document.dispatchEvent(new CustomEvent('aftercare:voice-start'));
    },
    () => {
      voiceButton.setState('processing');
      document.dispatchEvent(new CustomEvent('aftercare:voice-stop'));
    },
  );

  // Make voice button smaller for inline use
  const vbRoot = voiceButton.root;
  vbRoot.style.flexShrink = '0';
  voiceRow.appendChild(vbRoot);

  const voiceText = document.createElement('div');
  voiceText.style.cssText = 'flex: 1; min-width: 0;';

  const voicePrompt = document.createElement('div');
  voicePrompt.style.cssText = 'font-size: 15px; font-weight: 600; color: var(--color-text); line-height: 1.3;';
  voicePrompt.textContent = t('home.askAnything');
  voiceText.appendChild(voicePrompt);

  const voiceHint = document.createElement('div');
  voiceHint.style.cssText = 'font-size: 14px; color: var(--color-text-muted); margin-top: 2px;';
  voiceHint.textContent = t('home.voiceHint');
  voiceText.appendChild(voiceHint);

  voiceRow.appendChild(voiceText);
  wrapper.appendChild(voiceRow);

  // Set up voice event listeners with cleanup tracking
  const onIdle = (): void => voiceButton.setState('idle');
  const onError = (): void => voiceButton.setState('error');
  document.addEventListener('aftercare:voice-idle', onIdle);
  document.addEventListener('aftercare:voice-error', onError);
  homeScreenCleanup.push(() => {
    document.removeEventListener('aftercare:voice-idle', onIdle);
    document.removeEventListener('aftercare:voice-error', onError);
  });

  // ---- "How Is This Possible?" button — prominent CTA with eye-catching animation ----
  const howBtn = document.createElement('button');
  howBtn.setAttribute('role', 'link');
  howBtn.setAttribute('tabindex', '0');
  howBtn.style.cssText = `
    display: flex; align-items: center; justify-content: center; gap: 12px;
    width: 100%; padding: 18px 24px; border-radius: 16px;
    border: none;
    color: #fff; font-size: 17px; font-weight: 700;
    background: linear-gradient(135deg, #3d9d8f 0%, #2a7a6e 50%, #1d6b60 100%);
    cursor: pointer; font-family: var(--font-family);
    touch-action: manipulation;
    box-shadow: 0 4px 20px rgba(61,157,143,0.4), 0 0 0 0 rgba(61,157,143,0.5);
    animation: howBtnGlow 2s ease-in-out infinite;
    position: relative;
    overflow: hidden;
    min-height: 56px;
    letter-spacing: 0.02em;
  `;

  // Inject glow, shimmer, and arrow-bounce animations
  if (!document.getElementById('how-btn-anim-style')) {
    const style = document.createElement('style');
    style.id = 'how-btn-anim-style';
    style.textContent = `
      @keyframes howBtnGlow {
        0%, 100% {
          box-shadow: 0 4px 20px rgba(61,157,143,0.4), 0 0 0 0 rgba(61,157,143,0.5);
          transform: scale(1);
        }
        50% {
          box-shadow: 0 6px 28px rgba(61,157,143,0.6), 0 0 0 8px rgba(61,157,143,0.08);
          transform: scale(1.015);
        }
      }
      @keyframes howBtnShimmer {
        0% { transform: translateX(-100%) skewX(-15deg); }
        100% { transform: translateX(200%) skewX(-15deg); }
      }
      @keyframes howBtnArrow {
        0%, 60%, 100% { transform: translateX(0); }
        30% { transform: translateX(6px); }
      }
    `;
    document.head.appendChild(style);
  }

  // Shimmer sweep overlay
  const shimmer = document.createElement('span');
  shimmer.style.cssText = `
    position: absolute; top: 0; bottom: 0; left: 0; width: 60%;
    background: linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.15) 40%, rgba(255,255,255,0.25) 50%, rgba(255,255,255,0.15) 60%, transparent 100%);
    animation: howBtnShimmer 3.5s ease-in-out infinite;
    pointer-events: none;
  `;
  howBtn.appendChild(shimmer);

  const sparkle = document.createElement('span');
  sparkle.textContent = '\u2728';
  sparkle.style.cssText = 'font-size: 20px; position: relative; z-index: 1;';
  howBtn.appendChild(sparkle);

  const btnText = document.createElement('span');
  btnText.textContent = t('home.howPossible');
  btnText.style.cssText = 'position: relative; z-index: 1;';
  howBtn.appendChild(btnText);

  const arrow = document.createElement('span');
  arrow.textContent = '\u2192';
  arrow.style.cssText = 'font-size: 20px; position: relative; z-index: 1; animation: howBtnArrow 2s ease-in-out infinite;';
  howBtn.appendChild(arrow);

  howBtn.addEventListener('click', () => {
    window.open('/how-it-works.html', '_blank');
  });
  wrapper.appendChild(howBtn);

  // Footer with version + copyright
  const footer = document.createElement('div');
  footer.style.cssText = 'text-align: center; padding: 4px 0 var(--space-lg); line-height: 1.6;';

  const versionLine = document.createElement('div');
  versionLine.style.cssText = 'font-size: 10px; color: var(--color-text-dim); opacity: 0.4;';
  versionLine.textContent = `Bridgepoint AfterCare v${__APP_VERSION__}`;
  footer.appendChild(versionLine);

  const copyrightLine = document.createElement('div');
  copyrightLine.style.cssText = 'font-size: 10px; color: var(--color-text-dim); opacity: 0.4; margin-top: 2px;';
  copyrightLine.textContent = '\u00A9 2026 ';
  const isoLink = document.createElement('a');
  isoLink.href = 'https://isovision.ai';
  isoLink.target = '_blank';
  isoLink.rel = 'noopener';
  isoLink.style.cssText = 'color: var(--color-primary); text-decoration: none;';
  isoLink.textContent = 'ISOVision.ai';
  copyrightLine.appendChild(isoLink);
  copyrightLine.appendChild(document.createTextNode(' \u2014 ' + t('home.allRightsReserved')));
  footer.appendChild(copyrightLine);

  wrapper.appendChild(footer);

  container.appendChild(wrapper);
}
